import { useState, useRef, useCallback, useMemo } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { RotateCcw, Delete } from 'lucide-react';
import { useSession } from '../SessionContext';
import { StepShell } from './StepShell';
import { FeedbackDisplay } from './FeedbackDisplay';
import { PromptRenderer, type TiptapNode } from '@/shared/components/PromptRenderer';
import { LoadingIcon } from '@/interfaces/session-player/ui';
import { cn } from '@/foundation/utils/cn';
import type { ClozeImageConfig } from '../types';

const MARKER_RADIUS = 0.025;
const ACTIVE_MARKER_RADIUS = 0.032;

export function ClozeImageStep() {
  const { state, dispatch, currentStep } = useSession();
  const imgRef = useRef<HTMLImageElement>(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [focusedBlankId, setFocusedBlankId] = useState<string | null>(null);

  const snapshot = currentStep?.stepSnapshotPublicJson;
  const config = snapshot?.config as ClozeImageConfig | undefined;
  const media = currentStep?.mediaRefsJson as { url: string }[] | undefined;
  const imageUrl = media?.[0]?.url;

  const blanks = useMemo(() => config?.blanks ?? [], [config?.blanks]);
  const mode = config?.mode ?? 'multiple_choice';
  const isMultipleChoice = mode === 'multiple_choice';
  const allOptions = useMemo(() => config?.options ?? [], [config?.options]);

  const answer = state.answer?.type === 'cloze_image' ? state.answer : null;
  const blanksMap = useMemo(() => answer?.blanks ?? {}, [answer?.blanks]);
  const phase = state.phase;
  const result = state.result;
  const blankResults = result?.blankResults;
  const isDisabled = phase === 'submitting' || phase === 'grading';
  const isGraded = phase === 'graded';

  const usedOptionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    if (!isMultipleChoice) return counts;
    for (const b of blanks) {
      const val = blanksMap[b.id];
      if (val) counts.set(val, (counts.get(val) || 0) + 1);
    }
    return counts;
  }, [isMultipleChoice, blanks, blanksMap]);

  const availablePool = useMemo(() => {
    if (!isMultipleChoice) return [];
    const remaining = new Map(usedOptionCounts);
    return allOptions.map((opt, i) => {
      const usedCount = remaining.get(opt) || 0;
      if (usedCount > 0) {
        remaining.set(opt, usedCount - 1);
        return { text: opt, index: i, isUsed: true };
      }
      return { text: opt, index: i, isUsed: false };
    });
  }, [isMultipleChoice, allOptions, usedOptionCounts]);

  const hasAnyAnswer = blanks.some((b) => (blanksMap[b.id] || '').length > 0);

  const handleBlankChange = useCallback(
    (blankId: string, value: string) => {
      if (isDisabled || isGraded) return;
      dispatch({
        type: 'UPDATE_ANSWER',
        answer: { type: 'cloze_image', blanks: { ...blanksMap, [blankId]: value } },
      });
    },
    [isDisabled, isGraded, dispatch, blanksMap]
  );

  const handleBubbleClick = useCallback(
    (optionText: string) => {
      if (isDisabled || isGraded) return;
      const targetBlank =
        focusedBlankId && !blanksMap[focusedBlankId]
          ? focusedBlankId
          : blanks.find((b) => !blanksMap[b.id])?.id;
      if (targetBlank) {
        handleBlankChange(targetBlank, optionText);
      }
    },
    [isDisabled, isGraded, focusedBlankId, blanksMap, blanks, handleBlankChange]
  );

  const handleSlotClear = useCallback(
    (blankId: string) => {
      if (isDisabled || isGraded) return;
      handleBlankChange(blankId, '');
    },
    [isDisabled, isGraded, handleBlankChange]
  );

  const handleUndo = useCallback(() => {
    if (isDisabled || isGraded) return;
    for (let i = blanks.length - 1; i >= 0; i--) {
      if (blanksMap[blanks[i].id]) {
        handleBlankChange(blanks[i].id, '');
        return;
      }
    }
  }, [isDisabled, isGraded, blanks, blanksMap, handleBlankChange]);

  const handleReset = useCallback(() => {
    if (isDisabled || isGraded) return;
    const cleared: Record<string, string> = {};
    for (const b of blanks) cleared[b.id] = '';
    dispatch({ type: 'UPDATE_ANSWER', answer: { type: 'cloze_image', blanks: cleared } });
  }, [isDisabled, isGraded, blanks, dispatch]);

  const handleMarkerClick = useCallback(
    (blankId: string) => {
      if (isGraded) return;
      setFocusedBlankId(blankId);
      const el = document.getElementById(`cloze-image-blank-${blankId}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    },
    [isGraded]
  );

  if (!imageUrl) {
    return (
      <StepShell>
        <div
          className="flex items-center justify-center py-12"
          style={{ color: 'var(--f-fg-muted)' }}
        >
          No image available for this step.
        </div>
      </StepShell>
    );
  }

  return (
    <StepShell>
      {snapshot?.prompt && (
        <PromptRenderer doc={snapshot.prompt as TiptapNode} media={[]} diagramJson={null} />
      )}

      <p
        className="text-sm mb-2"
        style={{ color: 'var(--f-fg-muted)', fontFamily: 'var(--f-font-sans)' }}
      >
        {isGraded ? 'Results' : 'Label the numbered parts'}
      </p>

      <div className="flex flex-col gap-4">
        {/* Image with numbered markers */}
        <div className="relative w-full max-w-2xl mx-auto select-none overflow-hidden rounded-xl">
          {!imgLoaded && (
            <div className="flex items-center justify-center py-12">
              <LoadingIcon size="md" />
            </div>
          )}
          <img
            ref={imgRef}
            src={imageUrl}
            alt="Question image"
            className={cn('block w-full', !imgLoaded && 'invisible absolute inset-0')}
            onLoad={() => setImgLoaded(true)}
            draggable={false}
          />

          {imgLoaded && (
            <svg
              className="absolute inset-0 h-full w-full"
              viewBox="0 0 1 1"
              preserveAspectRatio="none"
            >
              {/* Label cover overlays (for images with existing labels) */}
              {blanks.map((blank) => {
                if (!blank.width || !blank.height) return null;
                const blankResult = blankResults?.[blank.id];
                const coverColor = isGraded
                  ? blankResult?.isCorrect
                    ? 'var(--f-success-subtle)'
                    : 'var(--f-error-subtle)'
                  : 'var(--f-bg-raised)';
                return (
                  <rect
                    key={`cover-${blank.id}`}
                    x={blank.x - blank.width / 2}
                    y={blank.y - blank.height / 2}
                    width={blank.width}
                    height={blank.height}
                    fill={coverColor}
                    stroke={
                      isGraded
                        ? blankResult?.isCorrect
                          ? 'var(--f-success)'
                          : 'var(--f-error)'
                        : 'var(--f-border-strong)'
                    }
                    strokeWidth={0.003}
                    rx={0.005}
                  />
                );
              })}

              {/* Numbered markers */}
              {blanks.map((blank, i) => {
                const isFocused = focusedBlankId === blank.id;
                const blankResult = blankResults?.[blank.id];
                const radius = isFocused ? ACTIVE_MARKER_RADIUS : MARKER_RADIUS;

                let fillColor = 'var(--f-accent)';
                if (isGraded && blankResult) {
                  fillColor = blankResult.isCorrect ? 'var(--f-success)' : 'var(--f-error)';
                }

                return (
                  <g
                    key={blank.id}
                    style={{ cursor: isGraded ? 'default' : 'pointer' }}
                    onClick={() => handleMarkerClick(blank.id)}
                  >
                    <circle
                      cx={blank.x}
                      cy={blank.y}
                      r={radius}
                      fill={fillColor}
                      stroke="white"
                      strokeWidth={0.004}
                    />
                    <text
                      x={blank.x}
                      y={blank.y}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fill="white"
                      fontSize={0.025}
                      fontWeight="bold"
                      fontFamily="var(--f-font-sans)"
                      style={{ pointerEvents: 'none' }}
                    >
                      {i + 1}
                    </text>
                  </g>
                );
              })}

              {/* Post-grading: show correct labels */}
              {isGraded &&
                blankResults &&
                blanks.map((blank) => {
                  const blankResult = blankResults[blank.id];
                  if (!blankResult) return null;
                  return (
                    <text
                      key={`label-${blank.id}`}
                      x={blank.x}
                      y={blank.y + MARKER_RADIUS + 0.025}
                      textAnchor="middle"
                      fill={blankResult.isCorrect ? 'var(--f-success)' : 'var(--f-error)'}
                      fontSize={0.02}
                      fontWeight="600"
                      fontFamily="var(--f-font-sans)"
                      style={{ pointerEvents: 'none' }}
                    >
                      {blankResult.correctAnswer}
                    </text>
                  );
                })}
            </svg>
          )}
        </div>

        {/* Answer panel */}
        <div className="flex flex-col gap-2 w-full max-w-2xl mx-auto">
          {blanks.map((blank, i) => {
            const value = blanksMap[blank.id] ?? '';
            const blankResult = blankResults?.[blank.id];
            const isFocused = focusedBlankId === blank.id;

            return (
              <div
                key={blank.id}
                id={`cloze-image-blank-${blank.id}`}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 transition-all duration-200',
                  isFocused && !isGraded && 'ring-2 ring-offset-1'
                )}
                style={{
                  backgroundColor: isGraded
                    ? blankResult?.isCorrect
                      ? 'var(--f-success-subtle)'
                      : 'var(--f-error-subtle)'
                    : isFocused
                      ? 'var(--f-bg-raised)'
                      : 'var(--f-bg-sunken)',
                  fontFamily: 'var(--f-font-sans)',
                  // @ts-expect-error CSS custom property
                  '--tw-ring-color': 'var(--f-accent)',
                }}
                onClick={() => !isGraded && setFocusedBlankId(blank.id)}
              >
                <span
                  className="flex-shrink-0 flex items-center justify-center rounded-full font-bold text-sm"
                  style={{
                    width: 28,
                    height: 28,
                    backgroundColor: isGraded
                      ? blankResult?.isCorrect
                        ? 'var(--f-success)'
                        : 'var(--f-error)'
                      : 'var(--f-accent)',
                    color: 'white',
                  }}
                >
                  {i + 1}
                </span>

                {isGraded && blankResult ? (
                  <div className="flex flex-col">
                    <span
                      style={{
                        color: blankResult.isCorrect
                          ? 'var(--f-success-text)'
                          : 'var(--f-error-text)',
                        fontWeight: 500,
                        textDecoration: blankResult.isCorrect ? 'none' : 'line-through',
                      }}
                    >
                      {value || '—'}
                    </span>
                    {!blankResult.isCorrect && (
                      <span
                        style={{
                          fontSize: 'var(--f-text-sm)',
                          color: 'var(--f-success-text)',
                          fontWeight: 500,
                        }}
                      >
                        {blankResult.correctAnswer}
                      </span>
                    )}
                  </div>
                ) : isMultipleChoice ? (
                  <button
                    type="button"
                    disabled={isDisabled && !value}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (value) handleSlotClear(blank.id);
                    }}
                    className="flex-1 text-left rounded-lg px-3 py-1 transition-all cursor-pointer"
                    style={{
                      border: value
                        ? '2px solid var(--f-accent)'
                        : '2px dashed var(--f-border-strong)',
                      backgroundColor: value ? 'var(--f-accent)' : 'transparent',
                      color: value ? 'var(--f-fg-on-accent)' : 'var(--f-fg-muted)',
                      fontFamily: 'var(--f-font-sans)',
                    }}
                  >
                    {value || '___'}
                  </button>
                ) : (
                  <input
                    type="text"
                    value={value}
                    disabled={isDisabled || isGraded}
                    onChange={(e) => handleBlankChange(blank.id, e.target.value)}
                    onFocus={() => setFocusedBlankId(blank.id)}
                    placeholder="Type your answer..."
                    className="flex-1 rounded-lg px-3 py-1 border-2 outline-none transition-all focus:ring-2 focus:ring-offset-1"
                    style={{
                      borderColor: 'var(--f-border-strong)',
                      backgroundColor: 'var(--f-bg-raised)',
                      color: 'var(--f-fg)',
                      fontFamily: 'var(--f-font-sans)',
                      // @ts-expect-error CSS custom property
                      '--tw-ring-color': 'var(--f-accent)',
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* MC option bubbles */}
        {isMultipleChoice && allOptions.length > 0 && !isGraded && (
          <div className="w-full max-w-2xl mx-auto">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Available options">
              {availablePool.map((opt) => (
                <button
                  key={opt.index}
                  type="button"
                  disabled={isDisabled || opt.isUsed}
                  onClick={() => handleBubbleClick(opt.text)}
                  className="inline-flex items-center rounded-xl font-medium cursor-pointer active:scale-[0.97] focus:outline-none"
                  style={{
                    padding: 'var(--f-space-2) var(--f-space-4)',
                    fontSize: 'var(--f-text-base)',
                    fontFamily: 'var(--f-font-sans)',
                    border: '2px solid',
                    borderColor: opt.isUsed ? 'transparent' : 'var(--f-border-strong)',
                    backgroundColor: opt.isUsed ? 'var(--f-bg-sunken)' : 'var(--f-bg-raised)',
                    color: opt.isUsed ? 'var(--f-fg-muted)' : 'var(--f-fg)',
                    opacity: opt.isUsed ? 0.4 : isDisabled ? 0.5 : 1,
                    cursor: opt.isUsed || isDisabled ? 'not-allowed' : 'pointer',
                    transition:
                      'border-color 0.3s ease, background-color 0.3s ease, opacity 0.4s ease',
                  }}
                  aria-label={opt.isUsed ? `${opt.text} (used)` : opt.text}
                >
                  {opt.text}
                </button>
              ))}
            </div>

            <AnimatePresence>
              {hasAnyAnswer && (
                <motion.div
                  className="mt-3 flex items-center gap-2"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                >
                  <button
                    type="button"
                    disabled={isDisabled}
                    onClick={handleUndo}
                    className="flex items-center gap-1 rounded-lg px-3 py-1.5 cursor-pointer disabled:opacity-30"
                    style={{
                      fontSize: 'var(--f-text-sm)',
                      color: 'var(--f-fg-secondary)',
                      fontFamily: 'var(--f-font-sans)',
                    }}
                  >
                    <Delete size={14} /> Undo
                  </button>
                  <button
                    type="button"
                    disabled={isDisabled}
                    onClick={handleReset}
                    className="flex items-center gap-1 rounded-lg px-3 py-1.5 cursor-pointer disabled:opacity-30"
                    style={{
                      fontSize: 'var(--f-text-sm)',
                      color: 'var(--f-fg-secondary)',
                      fontFamily: 'var(--f-font-sans)',
                    }}
                  >
                    <RotateCcw size={14} /> Reset
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* FT: Reset */}
        {!isMultipleChoice && !isGraded && (
          <AnimatePresence>
            {hasAnyAnswer && (
              <motion.div
                className="w-full max-w-2xl mx-auto flex items-center gap-2"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <button
                  type="button"
                  disabled={isDisabled}
                  onClick={handleReset}
                  className="flex items-center gap-1 rounded-lg px-3 py-1.5 cursor-pointer disabled:opacity-30"
                  style={{
                    fontSize: 'var(--f-text-sm)',
                    color: 'var(--f-fg-secondary)',
                    fontFamily: 'var(--f-font-sans)',
                  }}
                >
                  <RotateCcw size={14} /> Reset
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>

      {isGraded && result && <FeedbackDisplay result={result} />}
    </StepShell>
  );
}
